import React, { useEffect, useState, useCallback, useMemo } from "react";
import { MuiMaterials } from '../@index.js';
import * as global from '../data/global';
import { ModalChrome, ModalBody, ModalContentTile, Spinner, SITE_WINDOW_IDS, outlinedActionButtonSx, containedActionButtonSx, CenteredLoadingOverlay, withScrollHintMenuProps, SiteSelectPhase } from './windowChrome/index.js';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import DoneIcon from '@mui/icons-material/Done';
import ReplayIcon from '@mui/icons-material/Replay';
import AddIcon from '@mui/icons-material/Add';
import { createApiEndpoint } from '../index.js';

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        return d.toLocaleString("en-us", global.dateFormat || { month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch {
        return String(dateStr);
    }
};

const ViewSiteIssuesModal = ({ isModalOpen, setIsModalOpen, userSelectedAllSites, initialSiteGuid, filterToSiteGuid }) => {
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentTheme, setCurrentTheme] = useState(global.gw.theme || 'dark');
    const [forceUpdate, setForceUpdate] = useState(0);
    const [editingNotesId, setEditingNotesId] = useState(null);
    const [editingNotesValue, setEditingNotesValue] = useState('');
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [sites, setSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState('');
    const [pickerSiteGuid, setPickerSiteGuid] = useState('');
    const [newIssue, setNewIssue] = useState('');
    const [newNotes, setNewNotes] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [addMessage, setAddMessage] = useState(null);
    const [sortBy, setSortBy] = useState('dateCreated');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchText, setSearchText] = useState('');
    const [addModalOpen, setAddModalOpen] = useState(false);

    const getThemeColor = useCallback((lightColor, darkColor) => {
        return currentTheme === 'light' ? lightColor : darkColor;
    }, [currentTheme]);

    const themeText = getThemeColor('#333333', '#f4cc8e');
    const themeBg = getThemeColor('#ffffff', '#2d2d2d');

    const modalTheme = useMemo(() => {
        const isLight = currentTheme === 'light';
        const text = isLight ? '#333333' : '#f4cc8e';
        const bg = isLight ? '#ffffff' : '#2d2d2d';
        const border = isLight ? '#cccccc' : 'rgba(244, 204, 142, 0.5)';
        const borderHover = isLight ? '#666666' : '#f4cc8e';
        return MuiMaterials.createTheme({
            palette: {
                mode: isLight ? 'light' : 'dark',
                text: { primary: text, secondary: text },
            },
            components: {
                MuiInputLabel: {
                    styleOverrides: {
                        root: { color: isLight ? '#666666' : '#a8abb1' },
                        shrink: { color: text },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: text,
                            backgroundColor: bg,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: borderHover },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: borderHover },
                        },
                        input: {
                            color: text,
                            WebkitTextFillColor: text,
                        },
                    },
                },
                MuiSelect: {
                    styleOverrides: {
                        select: {
                            color: `${text} !important`,
                            WebkitTextFillColor: `${text} !important`,
                        },
                        icon: { color: text },
                    },
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: {
                            color: text,
                            backgroundColor: bg,
                            '&:hover': {
                                backgroundColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(244, 204, 142, 0.1)',
                            },
                            '&.Mui-selected': {
                                color: text,
                                backgroundColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(244, 204, 142, 0.18)',
                            },
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: { backgroundColor: bg, color: text },
                    },
                },
            },
        });
    }, [currentTheme, forceUpdate]);

    const outlinedFieldSx = useMemo(() => ({
        '& .MuiInputLabel-root': { color: getThemeColor('#666666', '#a8abb1') },
        '& .MuiInputLabel-root.Mui-focused': { color: themeText },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': { color: themeText },
        '& .MuiOutlinedInput-root': {
            color: themeText,
            backgroundColor: themeBg,
            '& fieldset': { borderColor: getThemeColor('#cccccc', 'rgba(244,204,142,0.5)') },
            '&:hover fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
            '&.Mui-focused fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
            '& .MuiSvgIcon-root': { color: themeText },
        },
        '& .MuiInputBase-input': {
            color: `${themeText} !important`,
            WebkitTextFillColor: `${themeText} !important`,
        },
        '& .MuiSelect-select': {
            color: `${themeText} !important`,
            WebkitTextFillColor: `${themeText} !important`,
        },
        '& .MuiSelect-select.Mui-disabled': {
            color: `${themeText} !important`,
            WebkitTextFillColor: `${themeText} !important`,
            opacity: 0.7,
        },
    }), [getThemeColor, themeText, themeBg]);

    const selectMenuProps = useMemo(() => ({
        MenuProps: withScrollHintMenuProps({
            PaperProps: {
                sx: {
                    backgroundColor: themeBg,
                    color: themeText,
                    '& .MuiMenuItem-root': {
                        color: themeText,
                        WebkitTextFillColor: themeText,
                        '&:hover': {
                            backgroundColor: getThemeColor('rgba(0,0,0,0.04)', 'rgba(244, 204, 142, 0.1)'),
                        },
                        '&.Mui-selected': {
                            color: themeText,
                            backgroundColor: getThemeColor('rgba(0,0,0,0.08)', 'rgba(244, 204, 142, 0.18)'),
                        },
                    },
                },
            },
        }),
    }), [getThemeColor, themeText, themeBg]);

    const siteSelectRenderValue = useCallback((value) => {
        const site = sites.find((s) => s.siteGuid === value);
        const label = site?.siteName || value || '';
        return (
            <MuiMaterials.Typography
                component="span"
                sx={{
                    color: themeText,
                    WebkitTextFillColor: themeText,
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                }}
            >
                {label}
            </MuiMaterials.Typography>
        );
    }, [sites, themeText]);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder(column === 'site' ? 'asc' : column === 'status' ? 'asc' : 'desc');
        }
    };

    const sortedIssues = useMemo(() => {
        const arr = [...issues];
        const mult = sortOrder === 'asc' ? 1 : -1;
        arr.sort((a, b) => {
            if (sortBy === 'site') {
                const sa = (a.siteName || '').toLowerCase();
                const sb = (b.siteName || '').toLowerCase();
                return mult * sa.localeCompare(sb);
            }
            if (sortBy === 'dateCreated') {
                const da = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
                const db = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
                return mult * (da - db);
            }
            if (sortBy === 'status') {
                const aClosed = a.dateIssueResolved ? 1 : 0;
                const bClosed = b.dateIssueResolved ? 1 : 0;
                return mult * (aClosed - bClosed);
            }
            return 0;
        });
        return arr;
    }, [issues, sortBy, sortOrder]);

    const siteScopedIssues = useMemo(() => {
        if (!filterToSiteGuid) return sortedIssues;
        return sortedIssues.filter(
            (row) =>
                global.normalizeGuidForCompare(row.siteGuid) ===
                global.normalizeGuidForCompare(filterToSiteGuid),
        );
    }, [sortedIssues, filterToSiteGuid]);

    const filteredIssues = useMemo(() => {
        const q = (searchText || '').trim().toLowerCase();
        if (!q) return siteScopedIssues;
        return siteScopedIssues.filter(row => {
            const siteName = (row.siteName || '').toLowerCase();
            const issue = (row.issue || '').toLowerCase();
            const notes = (row.notes || '').toLowerCase();
            const createdStr = formatDate(row.dateCreated).toLowerCase();
            const resolvedStr = (row.dateIssueResolved ? formatDate(row.dateIssueResolved) : 'open').toLowerCase();
            return siteName.includes(q) || issue.includes(q) || notes.includes(q) || createdStr.includes(q) || resolvedStr.includes(q);
        });
    }, [siteScopedIssues, searchText]);

    useEffect(() => {
        const handleThemeChange = () => {
            setCurrentTheme(global.gw.theme || 'dark');
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    useEffect(() => {
        if (global.gw.themeChanged) {
            setCurrentTheme(global.gw.theme || 'dark');
            setForceUpdate(prev => prev + 1);
        }
    }, [global.gw.themeChanged]);

    useEffect(() => {
        const interval = setInterval(() => {
            const currentThemeValue = global.gw.theme || 'dark';
            if (currentThemeValue !== currentTheme) {
                setCurrentTheme(currentThemeValue);
                setForceUpdate(prev => prev + 1);
            }
        }, 100);
        return () => clearInterval(interval);
    }, [currentTheme]);

    const refreshIssues = useCallback(async () => {
        try {
            const data = await createApiEndpoint().fetchAllSiteIssuesForAdmin("direct");
            setIssues(Array.isArray(data) ? data : []);
        } catch {
            setIssues([]);
        }
    }, []);

    useEffect(() => {
        let siteList = global.gw.siteInfos && global.gw.siteInfos.length > 0
            ? [...global.gw.siteInfos]
            : (userSelectedAllSites || []).filter(s => s.isSelected).map(s => ({ siteGuid: s.siteId, siteName: s.siteName }));
        if (siteList.length === 0 && issues.length > 0) {
            const seen = new Set();
            siteList = issues.filter(i => i.siteGuid && !seen.has(i.siteGuid) && seen.add(i.siteGuid)).map(i => ({ siteGuid: i.siteGuid, siteName: i.siteName || i.siteGuid }));
        }
        const sorted = siteList.sort((a, b) => ((a.siteName || '').toLowerCase()).localeCompare((b.siteName || '').toLowerCase()));
        setSites(sorted);
        if (sorted.length === 0) return;
        if (initialSiteGuid) {
            const match = sorted.find(
                (s) =>
                    global.normalizeGuidForCompare(s.siteGuid) ===
                    global.normalizeGuidForCompare(initialSiteGuid),
            );
            if (match) {
                setSelectedSite(match.siteGuid);
                return;
            }
        }
    }, [global.gw.siteInfos, userSelectedAllSites, issues, initialSiteGuid]);

    useEffect(() => {
        if (!isModalOpen[0] || !initialSiteGuid) return;
        setSelectedSite(initialSiteGuid);
    }, [isModalOpen, initialSiteGuid]);

    useEffect(() => {
        if (isModalOpen[0] && global.gw.auth) {
            setLoading(true);
            setEditingNotesId(null);
            setAddMessage(null);
            setSearchText('');
            refreshIssues().finally(() => setLoading(false));
        } else if (!isModalOpen[0]) {
            setAddModalOpen(false);
            setAddMessage(null);
            if (!filterToSiteGuid && !initialSiteGuid) {
                setSelectedSite('');
                setPickerSiteGuid('');
            }
        }
    }, [isModalOpen, refreshKey, refreshIssues]);

    const triggerRefresh = useCallback(() => {
        setRefreshKey(k => k + 1);
    }, []);

    const handleSaveNotes = async (row) => {
        setActionLoadingId(row.customerSiteIssueId);
        const ok = await createApiEndpoint().updateSiteIssueNotes(row.customerSiteIssueId, editingNotesValue);
        setActionLoadingId(null);
        setEditingNotesId(null);
        if (ok) {
            triggerRefresh();
            window.dispatchEvent(new CustomEvent('siteIssuesRefreshed'));
        }
    };

    const handleResolve = async (row) => {
        setActionLoadingId(row.customerSiteIssueId);
        const ok = await createApiEndpoint().resolveSiteIssue(row.customerSiteIssueId);
        setActionLoadingId(null);
        if (ok) {
            triggerRefresh();
            window.dispatchEvent(new CustomEvent('siteIssuesRefreshed'));
        }
    };

    const handleReopen = async (row) => {
        setActionLoadingId(row.customerSiteIssueId);
        const ok = await createApiEndpoint().reopenSiteIssue(row.customerSiteIssueId);
        setActionLoadingId(null);
        if (ok) {
            triggerRefresh();
            window.dispatchEvent(new CustomEvent('siteIssuesRefreshed'));
        }
    };

    const handleAddIssue = async () => {
        if (!newIssue.trim()) {
            setAddMessage({ type: 'error', text: 'Issue is required.' });
            return;
        }
        if (!selectedSite) {
            setAddMessage({ type: 'error', text: 'Please select a site.' });
            return;
        }
        setIsAdding(true);
        setAddMessage(null);
        const ok = await createApiEndpoint().addSiteIssue(selectedSite, newIssue.trim(), newNotes.trim() || null);
        setIsAdding(false);
        if (ok) {
            setNewIssue('');
            setNewNotes('');
            setAddMessage(null);
            setAddModalOpen(false);
            triggerRefresh();
            window.dispatchEvent(new CustomEvent('siteIssuesRefreshed'));
        } else {
            setAddMessage({ type: 'error', text: 'Failed to add issue.' });
        }
    };

    const openAddModal = () => {
        setAddMessage(null);
        setAddModalOpen(true);
    };

    const renderModal = () => {
        const phase = (filterToSiteGuid || initialSiteGuid || selectedSite) ? "edit" : "select";
        return (
            <>
            <ModalChrome
                open={isModalOpen[0]}
                onClose={() => setIsModalOpen([false])}
                title="Site Issues"
                toolTitle="Issues"
                siteWindowId={SITE_WINDOW_IDS.issues}
                siteGuid={filterToSiteGuid || selectedSite || initialSiteGuid || null}
                showHeaderSiteSelect={phase === "edit"}
                onHeaderSiteChange={(guid) => {
                    if (guid) setSelectedSite(guid);
                }}
                headerFilterSite={(s) => !s.isHms}
                actions={
                    phase === "edit" ? (
                    <MuiMaterials.Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, minWidth: 0 }}>
                        {!loading && siteScopedIssues.length > 0 ? (
                            <MuiMaterials.TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search site, issue, notes..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                sx={{ width: { xs: 160, sm: 240, md: 320 }, flexShrink: 1 }}
                            />
                        ) : null}
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={openAddModal}
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0, ...outlinedActionButtonSx() }}
                        >
                            Add
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                    ) : null
                }
                className="siteFilterModal hide-scrollbar"
                loadingOverlay={phase === "edit" && loading ? <CenteredLoadingOverlay /> : null}
            >
                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={(guid) => {
                            setPickerSiteGuid(guid);
                            setSelectedSite(guid);
                        }}
                        onCancel={() => setIsModalOpen([false])}
                        filterSite={(s) => !s.isHms}
                    />
                ) : (
                <ModalBody className="siteFilterModalContent hide-scrollbar" sx={{ overflowY: "auto" }}>
                    <ModalContentTile sx={{ p: 0 }}>
                        {loading ? null : issues.length === 0 ? (
                            <MuiMaterials.Typography sx={{ padding: '24px', textAlign: 'center', color: getThemeColor('#666666', '#a8abb1') }}>
                                No site issues found.
                            </MuiMaterials.Typography>
                        ) : siteScopedIssues.length === 0 ? (
                            <MuiMaterials.Typography sx={{ padding: '24px', textAlign: 'center', color: getThemeColor('#666666', '#a8abb1') }}>
                                No issues for this site.
                            </MuiMaterials.Typography>
                        ) : filteredIssues.length === 0 ? (
                            <MuiMaterials.Typography sx={{ padding: '24px', textAlign: 'center', color: getThemeColor('#666666', '#a8abb1') }}>
                                No matching issues.
                            </MuiMaterials.Typography>
                        ) : (
                            <MuiMaterials.TableContainer sx={{ flex: 1, maxHeight: '100%', overflow: 'auto' }}>
                                <MuiMaterials.Table stickyHeader size="small">
                                    <MuiMaterials.TableHead>
                                        <MuiMaterials.TableRow>
                                            {!filterToSiteGuid ? (
                                                <MuiMaterials.TableCell sx={{ fontWeight: 600, background: getThemeColor('#f5f5f5', '#3d3d3d'), color: getThemeColor('#333333', '#f4cc8e') }}>
                                                    <MuiMaterials.TableSortLabel active={sortBy === 'site'} direction={sortOrder} onClick={() => handleSort('site')} sx={{ color: `${getThemeColor('#333333', '#f4cc8e')} !important`, '&:hover': { color: `${getThemeColor('#333333', '#f4cc8e')} !important` }, '& .MuiTableSortLabel-icon': { color: `${getThemeColor('#333333', '#f4cc8e')} !important` } }}>Site</MuiMaterials.TableSortLabel>
                                                </MuiMaterials.TableCell>
                                            ) : null}
                                            <MuiMaterials.TableCell sx={{ fontWeight: 600, background: getThemeColor('#f5f5f5', '#3d3d3d'), color: getThemeColor('#333333', '#f4cc8e') }}>Issue</MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={{ fontWeight: 600, background: getThemeColor('#f5f5f5', '#3d3d3d'), color: getThemeColor('#333333', '#f4cc8e') }}>Notes</MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={{ fontWeight: 600, background: getThemeColor('#f5f5f5', '#3d3d3d'), color: getThemeColor('#333333', '#f4cc8e') }}>
                                                <MuiMaterials.TableSortLabel active={sortBy === 'dateCreated'} direction={sortOrder} onClick={() => handleSort('dateCreated')} sx={{ color: `${getThemeColor('#333333', '#f4cc8e')} !important`, '&:hover': { color: `${getThemeColor('#333333', '#f4cc8e')} !important` }, '& .MuiTableSortLabel-icon': { color: `${getThemeColor('#333333', '#f4cc8e')} !important` } }}>Created</MuiMaterials.TableSortLabel>
                                            </MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={{ fontWeight: 600, background: getThemeColor('#f5f5f5', '#3d3d3d'), color: getThemeColor('#333333', '#f4cc8e') }}>
                                                <MuiMaterials.TableSortLabel active={sortBy === 'status'} direction={sortOrder} onClick={() => handleSort('status')} sx={{ color: `${getThemeColor('#333333', '#f4cc8e')} !important`, '&:hover': { color: `${getThemeColor('#333333', '#f4cc8e')} !important` }, '& .MuiTableSortLabel-icon': { color: `${getThemeColor('#333333', '#f4cc8e')} !important` } }}>Status</MuiMaterials.TableSortLabel>
                                            </MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={{ fontWeight: 600, background: getThemeColor('#f5f5f5', '#3d3d3d'), color: getThemeColor('#333333', '#f4cc8e') }}>Actions</MuiMaterials.TableCell>
                                        </MuiMaterials.TableRow>
                                    </MuiMaterials.TableHead>
                                    <MuiMaterials.TableBody>
                                        {filteredIssues.map((row, idx) => (
                                            <MuiMaterials.TableRow key={row.customerSiteIssueId ?? idx} sx={{ '&:nth-of-type(odd)': { background: getThemeColor('rgba(0,0,0,0.02)', 'rgba(255,255,255,0.02)') } }}>
                                                {!filterToSiteGuid ? (
                                                    <MuiMaterials.TableCell sx={{ color: getThemeColor('#333333', '#f4cc8e'), fontSize: '14px' }}>{row.siteName || '-'}</MuiMaterials.TableCell>
                                                ) : null}
                                                <MuiMaterials.TableCell sx={{ color: getThemeColor('#333333', '#f4cc8e'), fontSize: '14px', maxWidth: '200px' }}>{row.issue || '-'}</MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell sx={{ color: getThemeColor('#333333', '#f4cc8e'), fontSize: '14px', maxWidth: '220px', minWidth: '160px' }}>
                                                    {editingNotesId === row.customerSiteIssueId ? (
                                                        <MuiMaterials.Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                                            <MuiMaterials.TextField
                                                                size="small"
                                                                multiline
                                                                minRows={2}
                                                                maxRows={6}
                                                                value={editingNotesValue}
                                                                onChange={(e) => setEditingNotesValue(e.target.value)}
                                                                placeholder="Notes..."
                                                                variant="outlined"
                                                                sx={{
                                                                    flex: 1,
                                                                    width: '100%',
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '13px',
                                                                        color: getThemeColor('#333333', '#f4cc8e'),
                                                                    },
                                                                    '& .MuiOutlinedInput-root': {
                                                                        backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                                                                        '& fieldset': {
                                                                            borderColor: getThemeColor('#cccccc', 'rgba(244, 204, 142, 0.5)'),
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: getThemeColor('#666666', '#f4cc8e'),
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: getThemeColor('#666666', '#f4cc8e'),
                                                                        },
                                                                    },
                                                                    '& .MuiInputLabel-root': {
                                                                        color: getThemeColor('#666666', '#a8abb1'),
                                                                    },
                                                                }}
                                                            />
                                                            <MuiMaterials.Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
                                                                <MuiMaterials.IconButton size="small" onClick={() => handleSaveNotes(row)} disabled={actionLoadingId === row.customerSiteIssueId} sx={{ color: '#4caf50' }}>
                                                                    <CheckIcon fontSize="small" />
                                                                </MuiMaterials.IconButton>
                                                                <MuiMaterials.IconButton size="small" onClick={() => { setEditingNotesId(null); }} sx={{ color: getThemeColor('#666', '#aaa') }}>
                                                                    <CancelIcon fontSize="small" />
                                                                </MuiMaterials.IconButton>
                                                            </MuiMaterials.Box>
                                                        </MuiMaterials.Box>
                                                    ) : (
                                                        <MuiMaterials.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            {row.notes || '-'}
                                                            <MuiMaterials.IconButton size="small" onClick={() => { setEditingNotesId(row.customerSiteIssueId); setEditingNotesValue(row.notes || ''); }} sx={{ padding: '2px', color: getThemeColor('#666', '#aaa') }}>
                                                                <EditIcon sx={{ fontSize: 16 }} />
                                                            </MuiMaterials.IconButton>
                                                        </MuiMaterials.Box>
                                                    )}
                                                </MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell sx={{ color: getThemeColor('#333333', '#f4cc8e'), fontSize: '12px' }}>{formatDate(row.dateCreated)}</MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell sx={{ fontSize: '12px' }}>
                                                    {row.dateIssueResolved ? (
                                                        <MuiMaterials.Typography component="span" sx={{ color: '#4caf50' }}>Resolved {formatDate(row.dateIssueResolved)}</MuiMaterials.Typography>
                                                    ) : (
                                                        <MuiMaterials.Typography component="span" sx={{ color: '#f44336', fontWeight: 600 }}>Open</MuiMaterials.Typography>
                                                    )}
                                                </MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell sx={{ fontSize: '12px' }}>
                                                    {actionLoadingId === row.customerSiteIssueId ? (
                                                        <Spinner size="inline" />
                                                    ) : row.dateIssueResolved ? (
                                                        <MuiMaterials.Button size="small" startIcon={<ReplayIcon />} onClick={() => handleReopen(row)} sx={{ textTransform: 'none', fontSize: '12px', color: getThemeColor('#333333', '#f4cc8e') }}>
                                                            Reopen
                                                        </MuiMaterials.Button>
                                                    ) : (
                                                        <MuiMaterials.Button size="small" startIcon={<DoneIcon />} onClick={() => handleResolve(row)} sx={{ textTransform: 'none', fontSize: '12px', color: '#4caf50' }}>
                                                            Close
                                                        </MuiMaterials.Button>
                                                    )}
                                                </MuiMaterials.TableCell>
                                            </MuiMaterials.TableRow>
                                        ))}
                                    </MuiMaterials.TableBody>
                                </MuiMaterials.Table>
                            </MuiMaterials.TableContainer>
                        )}
                    </ModalContentTile>
                </ModalBody>
                )}
            </ModalChrome>

            <MuiMaterials.Modal
                open={addModalOpen}
                onClose={() => {
                    if (isAdding) return;
                    setAddModalOpen(false);
                    setAddMessage(null);
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: getThemeColor('rgba(255, 255, 255, 0.45)', 'rgba(0, 0, 0, 0.45)'),
                }}
            >
                <MuiMaterials.ThemeProvider theme={modalTheme}>
                    <MuiMaterials.Paper
                        elevation={0}
                        sx={{
                            width: 'min(520px, 92vw)',
                            p: 3,
                            outline: 'none',
                            border: `2px solid ${getThemeColor('#cccccc', '#f4cc8e4d')}`,
                            borderRadius: 1,
                            backgroundColor: themeBg,
                            color: themeText,
                        }}
                    >
                        <MuiMaterials.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            Add Issue
                        </MuiMaterials.Typography>
                        <MuiMaterials.Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <MuiMaterials.TextField
                                select
                                size="small"
                                variant="outlined"
                                label="Site"
                                value={selectedSite}
                                onChange={(e) => setSelectedSite(e.target.value)}
                                disabled={Boolean(filterToSiteGuid)}
                                fullWidth
                                sx={outlinedFieldSx}
                                InputProps={{
                                    style: {
                                        color: themeText,
                                        WebkitTextFillColor: themeText,
                                        backgroundColor: themeBg,
                                    },
                                }}
                                SelectProps={{
                                    ...selectMenuProps,
                                    renderValue: siteSelectRenderValue,
                                }}
                            >
                                {sites.map((s, i) => (
                                    <MuiMaterials.MenuItem key={i} value={s.siteGuid} sx={{ color: themeText }}>
                                        {s.siteName}
                                    </MuiMaterials.MenuItem>
                                ))}
                            </MuiMaterials.TextField>
                            <MuiMaterials.TextField
                                size="small"
                                variant="outlined"
                                label="Issue"
                                value={newIssue}
                                onChange={(e) => setNewIssue(e.target.value)}
                                placeholder="Describe the issue..."
                                fullWidth
                                sx={outlinedFieldSx}
                                InputProps={{
                                    style: { color: themeText, WebkitTextFillColor: themeText, backgroundColor: themeBg },
                                }}
                            />
                            <MuiMaterials.TextField
                                size="small"
                                variant="outlined"
                                label="Notes (optional)"
                                value={newNotes}
                                onChange={(e) => setNewNotes(e.target.value)}
                                placeholder="Additional notes..."
                                multiline
                                minRows={2}
                                maxRows={4}
                                fullWidth
                                sx={outlinedFieldSx}
                                InputProps={{
                                    style: { color: themeText, WebkitTextFillColor: themeText, backgroundColor: themeBg },
                                }}
                            />
                            {addMessage ? (
                                <MuiMaterials.Typography
                                    sx={{ fontSize: '13px', color: addMessage.type === 'success' ? '#4caf50' : '#f44336' }}
                                >
                                    {addMessage.text}
                                </MuiMaterials.Typography>
                            ) : null}
                            <MuiMaterials.Stack direction="row" spacing={1} justifyContent="flex-end">
                                <MuiMaterials.Button
                                    size="small"
                                    variant="outlined"
                                    sx={outlinedActionButtonSx()}
                                    onClick={() => {
                                        setAddModalOpen(false);
                                        setAddMessage(null);
                                    }}
                                    disabled={isAdding}
                                >
                                    Cancel
                                </MuiMaterials.Button>
                                <MuiMaterials.Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddIssue}
                                    disabled={isAdding}
                                    sx={containedActionButtonSx()}
                                >
                                    {isAdding ? 'Adding...' : 'Add'}
                                </MuiMaterials.Button>
                            </MuiMaterials.Stack>
                        </MuiMaterials.Box>
                    </MuiMaterials.Paper>
                </MuiMaterials.ThemeProvider>
            </MuiMaterials.Modal>
            </>
        );
    };

    return renderModal();
};

export default ViewSiteIssuesModal;
